import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ImagePlus, Loader2, RotateCcw } from "lucide-react";
import { errMsg } from "@/lib/api";
import { ACCEPT, fileKind, initUpload, MAX_FILES, rejectMsg, uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { btnPrimary, Panel } from "@/components/admin/AdminLayout";

const CONCURRENCY = 2;
let seq = 0;

const QueueItem = ({ item, onRetry, i }) => (
  <li className="rounded-md border border-zinc-800 bg-[#121214] p-3" data-testid={`upload-item-${i}`}>
    <div className="flex items-center gap-2">
      {item.status === "done" && <CheckCircle2 className="h-4 w-4 shrink-0 text-[#7ee08a]" />}
      {(item.status === "error" || item.status === "rejected") && <AlertTriangle className="h-4 w-4 shrink-0 text-[#ff6b5e]" />}
      {(item.status === "uploading" || item.status === "queued") && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#FFD23F]" />}
      <span className="min-w-0 flex-1 truncate text-sm text-zinc-200" data-testid="upload-item-name">{item.file.name}</span>
      <span className="text-xs text-zinc-400" data-testid="upload-item-percent">{item.status === "rejected" ? "" : `${Math.round(item.progress * 100)}%`}</span>
    </div>
    {item.status !== "rejected" && <Progress value={item.progress * 100} className="mt-2 h-2 bg-zinc-800" data-testid="upload-item-progress" />}
    {item.status === "rejected" && <p className="mt-1.5 text-sm text-[#ff6b5e]" data-testid="upload-item-error">{item.error}</p>}
    {item.status === "error" && (
      <button onClick={() => onRetry(item.key)} className="mt-2 flex min-h-[44px] w-full items-center gap-2 rounded-md bg-[#C8322B]/15 px-3 text-left text-sm font-medium text-[#ff8a80]" data-testid="upload-item-retry-button">
        <RotateCcw className="h-4 w-4" /> Upload failed — tap to retry
      </button>
    )}
    {item.status === "done" && item.kind === "video" && <p className="mt-1.5 text-xs text-zinc-400">Uploaded — converting video, this can take a minute.</p>}
  </li>
);

export const Uploader = ({ projectId, onUploaded, autoOpen = false }) => {
  const input = useRef(null);
  useEffect(() => {
    if (autoOpen) input.current?.click();
  }, [autoOpen]);
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const store = useRef({});

  const patch = (key, p) => setItems((cur) => cur.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const runOne = async (item) => {
    patch(item.key, { status: "uploading", error: "" });
    try {
      const media = await uploadFile({
        file: item.file,
        projectId,
        session: store.current[item.key],
        onSession: (s) => (store.current[item.key] = s),
        onProgress: (p) => patch(item.key, { progress: p }),
      });
      patch(item.key, { status: "done", progress: 1 });
      onUploaded(media);
    } catch (e) {
      const st = e.response?.status;
      if (st === 400) patch(item.key, { status: "rejected", error: errMsg(e) });
      else patch(item.key, { status: "error", error: errMsg(e) });
    }
  };

  const runQueue = async (list) => {
    const queue = [...list];
    const worker = async () => {
      while (queue.length) await runOne(queue.shift());
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  };

  const pick = (e) => {
    const all = Array.from(e.target.files || []);
    e.target.value = "";
    if (!all.length) return;
    setNote(all.length > MAX_FILES ? `You can add up to ${MAX_FILES} files at a time — the first ${MAX_FILES} were added.` : "");
    const fresh = all.slice(0, MAX_FILES).map((file) => {
      const kind = fileKind(file);
      return { key: `u${++seq}`, file, kind, progress: 0, status: kind ? "queued" : "rejected", error: kind ? "" : rejectMsg(file.name) };
    });
    setItems((cur) => [...fresh, ...cur.filter((x) => x.status !== "done" && x.status !== "rejected")]);
    reserveInOrder(fresh.filter((x) => x.kind));
  };

  // Init sessions one at a time so media order always matches selection order; chunks then upload in parallel.
  const reserveInOrder = async (list) => {
    const ready = [];
    for (const item of list) {
      try {
        store.current[item.key] = await initUpload(item.file, projectId);
        ready.push(item);
      } catch (e) {
        patch(item.key, { status: e.response?.status === 400 ? "rejected" : "error", error: errMsg(e) });
      }
    }
    runQueue(ready);
  };

  const retry = (key) => {
    const item = items.find((x) => x.key === key);
    if (item) runOne(item);
  };

  return (
    <Panel title="Add photos & videos" testId="uploader-panel">
      <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={pick} data-testid="media-file-input" />
      <Button type="button" onClick={() => input.current?.click()} className={`${btnPrimary} h-14 w-full text-lg`} data-testid="media-add-button">
        <ImagePlus className="h-5 w-5" /> Choose photos or videos
      </Button>
      <p className="mt-2 text-sm text-zinc-500">Up to 10 at a time. Photos: JPG, PNG, WEBP, HEIC (15 MB). Videos: MP4, MOV, WEBM (300 MB).</p>
      {note && <p className="mt-2 text-sm text-[#FFD23F]" data-testid="upload-limit-note">{note}</p>}
      {items.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="upload-queue">
          {items.map((it, i) => <QueueItem key={it.key} item={it} i={i} onRetry={retry} />)}
        </ul>
      )}
    </Panel>
  );
};
